import React, { useCallback, useMemo } from 'react';

import ym from 'react-yandex-metrika';

import { Redirect, useHistory, useLocation, useParams } from 'react-router';
import { NavLink } from 'react-router-dom';
import { useIntl } from 'react-intl';
// @ts-ignore
import { SUBJECTS_SLUGS } from '@yandex-int/k-common/constants';
import { Spin, Text, useGeneratePath, useStaticUrls } from 'platform-components';
import groupBy from 'lodash/groupBy';
import cn from 'classnames';
// @ts-ignore
import { DefaultClose } from 'platform-components/src/components/modal/__portal/modal__portal';
import { Drawer } from '@edu-frontend/modules/src/components/ui/Drawer';
import { Text as HrText } from '@edu-frontend/modules/src/components/ui/Text';
import { Button as HrButton } from '@edu-frontend/modules/src/components/ui/Button';
import { Tag } from '@edu-frontend/modules/src/components/ui/Tag';

import { ContainerModule, LessonGroup, SUBJECT_LIBRARY_TAB_SLUG } from '@yandex-int/k-common/typings';
import qs from 'query-string';
import { SEARCH_GROUP_INDEX, SEARCH_OPEN_MODULE } from 'common.components/constants';
import { useSelector } from 'react-redux';
import { getCurrentYearCourseId } from 'common.components/course/selectors';
import {
    IconDocument,
    IconCalendarGrid,
    IconGrid,
    IconCheckCircle,
    IconBookmarkChecked,
} from '@edu-frontend/modules/src/components/ui/Icon';
import { DEFAULT_GRADE_INFORMATICS } from 'common.components/user/user.constants';
import { useCourseContainerDescriptionQuery } from 'platform-components/src/queries/use-course-container-description-query';
import { useTeacherMainPageCourseContainersQuery } from 'common.components/teacher-main-page/queries';
import { useGradeFromQueryOrTeam } from '../../../../utils/use-current-grade';
import { Library__informatics__modules } from '../__modules/library__informatics__modules';
import { useOpenModuleFromLastAssignmentOrSearchParams } from '../../../library.course-container/course-container';
import { useCourseContainersQuery, useModulesQuery } from '../../../library.course-container/queries';

import './library__informatics__course-container.scss';

type Params = {
    subject: SUBJECTS_SLUGS;
    containerSlug: string;
};

export const Library__informatics__courseContainer = () => {
    return (
        <div>
            <CourseContainerAbout />
            <CourseContainerParagraphs />
        </div>
    );
};

const CourseContainerAbout = () => {
    const { containerSlug: slug } = useParams<Params>();
    const intl = useIntl();
    const grade = useGradeFromQueryOrTeam(DEFAULT_GRADE_INFORMATICS);
    const staticUrls = useStaticUrls();
    const { classId, subject } = useParams<{ classId?: string; subject: string }>();
    const generatePath = useGeneratePath();
    const history = useHistory();

    const courseContainers = useTeacherMainPageCourseContainersQuery({ grade });

    const courseContainer = useMemo(() => {
        return courseContainers.data?.course_container_info.find((cc) => cc.slug === slug);
    }, [courseContainers.data?.course_container_info, slug]);

    const detailsQuery = useCourseContainerDescriptionQuery({ slug });

    const { description_teaser, description_rest } = detailsQuery.data;

    if (courseContainers.isSuccess && !courseContainer) {
        const informaticsTabPath = classId ? staticUrls.labClassLibrarySubjectTab : staticUrls.deepLabLibrary;

        const informaticsTabUrl = `${generatePath(informaticsTabPath, {
            subject,
            tabSlug: SUBJECT_LIBRARY_TAB_SLUG.CONTAINER,
        })}${history.location.search}`;

        return <Redirect to={informaticsTabUrl} />;
    }

    if (!slug || !courseContainer) {
        return <Spin center />;
    }

    const secondaryLink = intl.formatMessage({ id: `courseModuleBanner.button.secondary.default.${slug}.link` });

    return (
        <div className="library__informatics_cc-about-container">
            <div className="library__informatics_cc-about-text">
                <HrText typography="titleL">{courseContainer.name}</HrText>
                {description_teaser && (
                    <Text size={Text.SIZE.XL} type={Text.TYPE.MARKDOWN}>
                        {description_teaser}
                    </Text>
                )}
                {description_rest && (
                    <Text size={Text.SIZE.XL} type={Text.TYPE.MARKDOWN}>
                        {description_rest}
                    </Text>
                )}
            </div>

            <div className="library__informatics_cc-about-details">
                <a href={secondaryLink} target="_blank">
                    <HrButton
                        icon={(className: string) => <IconDocument className={className} sizeManagement="parent" />}
                        size="s"
                        view="outline"
                    >
                        {intl.formatMessage({ id: 'courseModuleBanner.button.secondary.default' })}
                    </HrButton>
                </a>

                <HrButton
                    className="library__informatics_disabled-button"
                    icon={(className: string) => <IconCalendarGrid className={className} sizeManagement="parent" />}
                    size="s"
                    view="ghost"
                >
                    {intl.formatMessage({ id: `library.collection.${slug}.intensity` })}
                </HrButton>
                <HrButton
                    className="library__informatics_disabled-button"
                    icon={(className: string) => <IconGrid className={className} sizeManagement="parent" />}
                    size="s"
                    view="ghost"
                >
                    {intl.formatMessage({ id: `library.collection.${slug}.content` })}
                </HrButton>
            </div>
        </div>
    );
};

interface CourseContainerParagraphsProps {
    containerSlug?: string;
    isOnlyDrawer?: boolean;
}

export const CourseContainerParagraphs = (props: CourseContainerParagraphsProps) => {
    const history = useHistory();
    const { containerSlug: containerSlugFromUrl } = useParams<Params>();
    const { isOnlyDrawer, containerSlug: containerSlugFromProps } = props;

    const containerSlug = containerSlugFromProps ?? containerSlugFromUrl;

    const courseId = useSelector(getCurrentYearCourseId);
    const modulesQuery = useModulesQuery(containerSlug, { courseId, withLessons: true });
    const cmodules = modulesQuery.data?.cmodules;
    const courseContainersQuery = useCourseContainersQuery(courseId);
    const grade = useGradeFromQueryOrTeam(DEFAULT_GRADE_INFORMATICS);
    const groups = useMemo(() => Object.entries(groupBy(cmodules || [], 'group_name')), [cmodules]);
    const courseContainer = useMemo(
        () => courseContainersQuery.data?.find((cc) => cc.container.slug === containerSlug),
        [courseContainersQuery.data, containerSlug]
    );

    const lastAssignedLessonId = courseContainer?.module_lesson_link?.lesson?.id;
    const lastModuleIdWithLessonsAssign = courseContainer?.module_lesson_link?.module?.id;

    const { search, pathname } = useLocation();
    const params = qs.parse(search);

    const openedModule = useOpenModuleFromLastAssignmentOrSearchParams(
        courseId,
        courseContainersQuery.isSuccess,
        courseContainer,
        modulesQuery.isSuccess,
        modulesQuery.data
    );

    const closeModal = useCallback(() => {
        delete params[SEARCH_OPEN_MODULE];
        delete params[SEARCH_GROUP_INDEX];
        const link = `${pathname}?${qs.stringify(params)}`;
        history.push(link);
    }, [history, params, pathname]);

    const handleModuleClick = useCallback(
        (module: ContainerModule) => {
            ym('reachGoal', 'teacher_view_content', { grade, module_id: module.module.id });
        },
        [grade]
    );
    const handleLessonsGroupClick = useCallback(
        (module: ContainerModule, groupName: string) => {
            ym('reachGoal', 'teacher_view_content', {
                grade,
                lesson_group_name: groupName,
                module_id: module.module.id,
            });
        },
        [grade]
    );

    if (!modulesQuery.isSuccess) {
        return <Spin />;
    }

    return (
        <div>
            <Drawer
                className="library__informatics_cc-drawer"
                direction="right"
                onClose={closeModal}
                visible={Boolean(openedModule && modulesQuery.data)}
                zIndex={100}
            >
                {openedModule && modulesQuery.data && (
                    <div className="library__informatics_cc_modal_top-level-content">
                        <DefaultClose onClick={closeModal} />
                        <Library__informatics__modules modules={modulesQuery.data} />
                    </div>
                )}
            </Drawer>
            {!isOnlyDrawer && <HrText typography="titleM">Содержание программы</HrText>}
            {!isOnlyDrawer && (
                <ol className="library__informatics_cc_list">
                    {groups.map(([groupName, modules], index) => {
                        return (
                            <li key={groupName}>
                                <div className="library__informatics_cc_list-item">
                                    <div className="library__informatics_cc_list-item-spacer">
                                        <Tag size="s" text={index + 1} />
                                    </div>
                                    <HrText color="secondary" typography="titleM">
                                        {groupName}
                                    </HrText>
                                </div>
                                <ol className="library__informatics_cc_list-modules">
                                    {modules.map((module, index2) => {
                                        const isLastAssign = module.module.id === lastModuleIdWithLessonsAssign;
                                        return (
                                            <CourseContainerModule
                                                index={index}
                                                index2={index2}
                                                key={module.module.id}
                                                lastAssignedLessonId={isLastAssign ? lastAssignedLessonId : undefined}
                                                module={module}
                                                onLessonsGroupClick={handleLessonsGroupClick}
                                                onModuleClick={handleModuleClick}
                                            />
                                        );
                                    })}
                                </ol>
                            </li>
                        );
                    })}
                </ol>
            )}
        </div>
    );
};

type ModuleProps = {
    /**
     * Данные модуля
     */

    module: ContainerModule;
    /**
     * Индекс первого уровня (Параграф), начиная с 0
     */

    index: number;
    /**
     * Индекс второго уровня (Модуль), начиная с 0
     */
    index2: number;

    /**  id последнего выданного урока */
    lastAssignedLessonId?: number;

    onModuleClick?: (module: ContainerModule) => void;

    onLessonsGroupClick?: (module: ContainerModule, groupName: string) => void;
};

type LessonGroupProps = {
    /**
     * Id модуля
     */
    moduleId: number;
    /**
     * Название группы уроков в Markdown
     */
    groupName: string;
    /**
     * Индекс третьего уровня (Группа уроков = Урок), начиная с 0
     */
    index3: number;
    status?: LessonGroupStatuses;
    onClick?: () => void;
};

const STATUS_BADGE_MAP = {
    default: '',
    finished: '',
    lastAssign: (
        <div className="library__informatics_cc__badge library__informatics_cc__assigned-badge">
            <IconBookmarkChecked className="library__informatics_cc__assigned-badge-svg" size={'s'} />
        </div>
    ),
    assigned: (
        <div className="library__informatics_cc__badge library__informatics_cc__finished-badge">
            <IconCheckCircle size={'s'} />
        </div>
    ),
} as const satisfies Record<string, React.ReactNode>;

type LessonGroupStatuses = keyof typeof STATUS_BADGE_MAP;

const CourseContainerLessonGroup = ({ groupName, moduleId, index3, status = 'default', onClick }: LessonGroupProps) => {
    const { search, pathname } = useLocation();
    const params = qs.parse(search);
    params[SEARCH_OPEN_MODULE] = String(moduleId);
    params[SEARCH_GROUP_INDEX] = String(index3);
    const link = `${pathname}?${qs.stringify(params)}`;

    const statusBadge = STATUS_BADGE_MAP[status];
    return (
        <NavLink className="library__informatics_cc_list-item" onClick={onClick} to={link}>
            <div className="library__informatics_cc_list-item-spacer">{statusBadge}</div>
            <div className="library__informatics_cc_lesson-group">
                <Text size={Text.SIZE.M} type={Text.TYPE.MARKDOWN}>
                    {groupName}
                </Text>
            </div>
        </NavLink>
    );
};

const CourseContainerModule = ({
    module,
    index,
    index2,
    lastAssignedLessonId,
    onModuleClick,
    onLessonsGroupClick,
}: ModuleProps) => {
    const { search, pathname } = useLocation();
    const params = qs.parse(search);
    params[SEARCH_OPEN_MODULE] = String(module.module.id);
    delete params[SEARCH_GROUP_INDEX];
    const link = `${pathname}?${qs.stringify(params)}`;

    const isLocked = module.lessons_count === 0;

    const lessonsGroups: [{ groupName: string; lessonStatus: LessonGroupStatuses | undefined }, LessonGroup[]][] =
        useMemo(() => {
            return Object.entries(groupBy(module.lesson_groups || [], 'group_name')).map(
                ([groupName, lessonGroups]) => {
                    let lessonStatus: LessonGroupStatuses | undefined;

                    // определяем количество выдач и завершений уроков в группе
                    const lessonStatusCounters = {
                        assigned: 0,
                        finished: 0,
                    };
                    lessonGroups.forEach((lessonGroup) => {
                        lessonStatusCounters.assigned += lessonGroup.assigned_count ?? 0;
                        lessonStatusCounters.finished += lessonGroup.finished_count ?? 0;
                    });

                    // есть ли в группе последний выданный урок курс-контейнера
                    const isLessonsGroupWithLastAssignedLesson = !!lessonGroups.some(
                        ({ lesson }) => lesson.id === lastAssignedLessonId
                    );

                    if (lessonStatusCounters.assigned > 0) {
                        lessonStatus = 'assigned';
                    }

                    if (isLessonsGroupWithLastAssignedLesson) {
                        lessonStatus = 'lastAssign';
                    }
                    return [{ groupName, lessonStatus }, lessonGroups];
                }
            );
        }, [module.lesson_groups, lastAssignedLessonId]);

    const handleModuleClick = useCallback(() => {
        onModuleClick?.(module);
    }, [module, onModuleClick]);

    return (
        <li>
            {isLocked ? (
                <div className="library__informatics_cc_list-item">
                    <div className="library__informatics_cc_list-item-spacer">
                        <Tag size="s" text={`${index + 1}.${index2 + 1}`} />
                    </div>
                    <div>
                        <HrText color="secondary" typography="titleS">
                            {module.module.name}
                        </HrText>
                        <HrText color="secondary" typography="bodyS">
                            Модуль в разработке и скоро станет доступен
                        </HrText>
                    </div>
                </div>
            ) : (
                <NavLink className="library__informatics_cc_list-item" onClick={handleModuleClick} to={link}>
                    <div className="library__informatics_cc_list-item-spacer">
                        <Tag
                            checked
                            className={cn(lastAssignedLessonId && 'library__informatics_cc__assigned-tag')}
                            size="s"
                            text={`${index + 1}.${index2 + 1}`}
                        />
                    </div>
                    <HrText color="primary" typography="titleS">
                        {module.module.name}
                    </HrText>
                </NavLink>
            )}

            {module.lesson_groups && (
                <div>
                    {lessonsGroups.map((lessonGroup, i) => {
                        const { groupName, lessonStatus } = lessonGroup[0];

                        return (
                            <CourseContainerLessonGroup
                                groupName={groupName}
                                index3={i}
                                key={i}
                                moduleId={module.module.id}
                                onClick={() => onLessonsGroupClick?.(module, groupName)}
                                status={lessonStatus}
                            />
                        );
                    })}
                </div>
            )}
        </li>
    );
};
